import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Form,
  Input,
  Select,
  Tag,
  Tooltip,
  Popover,
  Button,
  Typography,
  Upload,
  Checkbox,
  Alert,
  Spin,
  message,
} from "antd";
import {
  CloseCircleOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  CopyOutlined,
  BankOutlined,
  UserOutlined,
  DollarOutlined,
  WarningOutlined,
  ReloadOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useUserPanel } from "../../contexts/UserPanelContext";
import { useRequestDetailsStore } from "../../shared/stores/useRequestDetailsStore";
import { useRequestsStore } from "../../shared/stores/useRequestsStore";
import { usePanelsStore } from "../../shared/stores/usePanelsStore";
import { getAndSanitize } from "../../shared/utils/sanitization";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_BASE_URL } from "../../constants/config";
import type { UploadFile } from "antd/lib/upload/interface";
import PanelSelector from "../../shared/components/PanelSelector";
import "./styles.css";

const CANCELLATION_REASONS = {
  INSUFFICIENT_BALANCE: "Saldo insuficiente",
  USER_REQUEST: "Solicitud del usuario",
  SUSPICIOUS_ACTIVITY: "Actividad sospechosa",
  OTHER: "Otro motivo",
} as const;

export const WithdrawCompletionPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { t } = useTranslation();
  const { currentPanelId, checkHistory } = useUserPanel();

  // Theme class helper
  const tc = (base: string) => `${base} ${base}--${isDark ? "dark" : "light"}`;

  const [form] = Form.useForm();
  const [cancelForm] = Form.useForm();

  // Data loading state
  const [fullRequestData, setFullRequestData] = useState<any>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // UI state
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showChangeAmount, setShowChangeAmount] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showCustomReason, setShowCustomReason] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [notifyUser, setNotifyUser] = useState(false);
  const [copySuccess, setCopySuccess] = useState<Record<string, boolean>>({});

  // History state
  const [historyData, setHistoryData] = useState<any[] | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Panel selector state
  const [panelSelectorOpen, setPanelSelectorOpen] = useState(false);
  const [availablePanels, setAvailablePanels] = useState<Array<{ id: string; name?: string; isActive?: boolean }>>([]);
  const [isLoadingPanels] = useState(false);
  const [pendingPanelOperation, setPendingPanelOperation] = useState<{
    type: "submit" | "history" | null;
    values?: any;
  }>({ type: null });

  // Derived state
  const isReprocessing = useMemo(() => {
    return fullRequestData?.status === "REJECTED" || fullRequestData?.status === "CANCELED";
  }, [fullRequestData?.status]);

  const rejectionReason = useMemo(() => {
    return fullRequestData?.rejectionReason || "Motivo no especificado";
  }, [fullRequestData?.rejectionReason]);

  const bankInfo = useMemo(() => {
    const rawAmount = fullRequestData?.withdrawAmount ?? fullRequestData?.amount;
    const amount = typeof rawAmount === "number" ? rawAmount : parseFloat(rawAmount || "0") || 0;
    return {
      cbu: fullRequestData?.withdrawCvuCbu || "",
      alias: fullRequestData?.withdrawAlias || "",
      holder: fullRequestData?.withdrawHolder || "",
      amount,
    };
  }, [fullRequestData]);

  // --- Resolve panel ID ---
  const resolvePanelId = useCallback(async (): Promise<string | null> => {
    if (currentPanelId) return currentPanelId;

    const savedPanelId = getAndSanitize("panelId");
    if (savedPanelId) return savedPanelId;

    try {
      const panels = await usePanelsStore.getState().fetchPanels(true);
      if (panels.length === 1) return panels[0].id;
      if (panels.length > 1) {
        message.error("Seleccione un panel para continuar");
        return null;
      }
      message.error("No hay paneles disponibles");
      return null;
    } catch {
      message.error("Error al obtener paneles");
      return null;
    }
  }, [currentPanelId]);

  // --- Fetch request details ---
  useEffect(() => {
    const loadRequestDetails = async () => {
      if (!requestId) return;

      setIsLoadingDetails(true);
      try {
        const store = useRequestsStore.getState();
        const allRequests = [
          ...store.pendingRequests,
          ...store.approvedWithdraws,
          ...store.completedRejected,
        ];
        const storeRecord: any = allRequests.find((r: any) => r.id === requestId);

        let detailsData: any = null;
        const token = getAndSanitize("token");
        const adminId = currentPanelId || getAndSanitize("adminId") || getAndSanitize("userId");
        if (token && adminId) {
          try {
            detailsData = await useRequestDetailsStore
              .getState()
              .fetchDetails(requestId, adminId, { forceRefresh: true });
          } catch {
            // Details endpoint is designed for LOAD requests; may not return withdraw fields
          }
        }

        const merged = {
          ...(detailsData || {}),
          ...(storeRecord || {}),
          requestData: detailsData?.requestData || storeRecord?.requestData,
        };

        if (merged.id || merged.status) {
          setFullRequestData(merged);

          const withdrawAmt = merged.withdrawAmount || merged.amount;
          form.setFieldsValue({
            amount: withdrawAmt ? Number(withdrawAmt) : 0,
          });

          if (merged.status === "REJECTED" || merged.status === "CANCELED") {
            setShowChangeAmount(true);
          }
        }
      } catch (error) {
        console.error("[WithdrawCompletionPage] Error loading details:", error);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    loadRequestDetails();
  }, [requestId, currentPanelId, form]);

  // --- Fetch history on mount ---
  const fetchHistory = useCallback(async (panelId?: string) => {
    if (!fullRequestData?.user?.username) return;

    setIsLoadingHistory(true);
    try {
      const panels = await usePanelsStore.getState().fetchPanels();
      if (!panels || panels.length === 0) {
        message.error("No hay paneles disponibles");
        return;
      }

      const targetPanelId = panelId || (panels.length === 1 ? panels[0].id : null);

      if (!targetPanelId) {
        setAvailablePanels(panels);
        setPendingPanelOperation({ type: "history" });
        setPanelSelectorOpen(true);
        setIsLoadingHistory(false);
        return;
      }

      const result: any = await checkHistory({
        username: fullRequestData.user.username,
        panelId: targetPanelId,
      });
      setHistoryData(result?.history || []);
    } catch {
      message.error("Error al obtener historial");
    } finally {
      setIsLoadingHistory(false);
    }
  }, [fullRequestData?.user?.username, checkHistory]);

  // Auto-fetch history when data loads
  useEffect(() => {
    if (fullRequestData?.user?.username) {
      fetchHistory();
    }
  }, [fullRequestData?.user?.username]);

  // --- Handlers ---
  const handleBack = useCallback(() => navigate("/user-panel/requests"), [navigate]);

  const handleFileChange = useCallback(({ fileList }: { fileList: UploadFile[] }) => {
    const newFileList = fileList.slice(-1);
    setFileList(newFileList);

    if (newFileList[0]?.originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target?.result as string);
      reader.readAsDataURL(newFileList[0].originFileObj);
    } else {
      setPreviewUrl(null);
    }
  }, []);

  const copyToClipboard = useCallback((text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess((prev) => ({ ...prev, [field]: true }));
      setTimeout(() => setCopySuccess((prev) => ({ ...prev, [field]: false })), 2000);
    });
  }, []);

  // --- Submit withdraw completion ---
  const handleSubmit = useCallback(async (values: any) => {
    if (!requestId || !fullRequestData) return;

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("requestId", requestId);
    formData.append("username", fullRequestData.user?.username || "");
    formData.append("amount", String(Number(values.amount)));
    formData.append("notifyUser", String(notifyUser));
    formData.append("changeAmount", String(showChangeAmount || isReprocessing));

    if (fullRequestData.transactionId) {
      formData.append("transactionId", fullRequestData.transactionId);
    }

    let panelId = currentPanelId;
    if (!panelId) {
      panelId = await resolvePanelId();
    }
    if (!panelId) {
      setIsSubmitting(false);
      return;
    }
    formData.append("panelId", panelId);

    const adminId = fullRequestData.adminId || getAndSanitize("adminId");
    if (!adminId) {
      message.error("Admin ID is required");
      setIsSubmitting(false);
      return;
    }
    formData.append("adminId", adminId);

    const fileObj = fileList[0]?.originFileObj;
    if (fileObj) {
      formData.append("receipt", fileObj);
    }

    try {
      const endpoint = isReprocessing
        ? `${API_BASE_URL}/api/withdraw-requests/withdraw-requests/reprocess`
        : `${API_BASE_URL}/api/withdraw-requests/withdraw-requests/complete`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${getAndSanitize("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        await new Promise((resolve) => setTimeout(resolve, 600));
        message.success(
          isReprocessing
            ? t("requestsPanel.notifications.success.withdrawReprocessed") || "Retiro reprocesado con éxito"
            : t("requestsPanel.notifications.success.withdrawCompleted") || "Retiro completado con éxito"
        );
        navigate("/user-panel/requests");
      } else {
        throw new Error(response.data.message || "Error processing request");
      }
    } catch (error: any) {
      console.error("[WithdrawCompletionPage] Error processing:", error);
      message.error(
        error.response?.data?.message ||
        t("requestsPanel.notifications.error.withdrawError") ||
        "Error processing withdraw"
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [requestId, fullRequestData, currentPanelId, fileList, isReprocessing, notifyUser, showChangeAmount, resolvePanelId, navigate, t]);

  // --- Cancel withdraw ---
  const handleCancel = useCallback(async (values: { rejectionReason: string; customReason?: string }) => {
    if (!requestId || !currentPanelId) return;

    setIsCancelling(true);
    const reason = values.rejectionReason === "Otro motivo" ? values.customReason : values.rejectionReason;

    try {
      await axios.post(
        `${API_BASE_URL}/api/withdraw-requests/withdraw-requests/cancel`,
        {
          requestId,
          rejectionReason: reason,
          panelId: currentPanelId,
        },
        {
          headers: { Authorization: `Bearer ${getAndSanitize("token")}` },
        }
      );

      await new Promise((resolve) => setTimeout(resolve, 600));
      message.success(t("requestsPanel.notifications.success.withdrawCanceled"));
      navigate("/user-panel/requests");
    } catch (error: any) {
      console.error("[WithdrawCompletionPage] Cancel error:", error);
      message.error(
        error.response?.data?.message ||
        t("requestsPanel.notifications.error.withdrawCancelError")
      );
    } finally {
      setIsCancelling(false);
    }
  }, [requestId, currentPanelId, navigate, t]);

  // --- Panel selector handler ---
  const handlePanelSelect = useCallback(async (panelId: string) => {
    setPanelSelectorOpen(false);

    if (pendingPanelOperation.type === "history") {
      fetchHistory(panelId);
    }

    setPendingPanelOperation({ type: null });
  }, [pendingPanelOperation, fetchHistory]);

  // --- Cancel popover content ---
  const cancelFormContent = (
    <Form form={cancelForm} layout="vertical" onFinish={handleCancel} style={{ width: 300 }}>
      <Form.Item name="rejectionReason" rules={[{ required: true, message: "Seleccione un motivo" }]} style={{ marginBottom: 8 }}>
        <Select placeholder="Motivo de cancelación" onChange={(v) => setShowCustomReason(v === CANCELLATION_REASONS.OTHER)}>
          {Object.entries(CANCELLATION_REASONS).map(([k, v]) => (
            <Select.Option key={k} value={v}>{v}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      {showCustomReason && (
        <Form.Item name="customReason" rules={[{ required: true, message: "Ingrese su motivo" }]} style={{ marginBottom: 8 }}>
          <Input.TextArea placeholder="Ingrese su motivo" autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <Button size="small" onClick={() => { setShowCancelForm(false); setShowCustomReason(false); cancelForm.resetFields(); }}>Volver</Button>
        <Button danger type="primary" size="small" htmlType="submit" loading={isCancelling}>Confirmar</Button>
      </div>
    </Form>
  );

  // --- Loading state ---
  if (isLoadingDetails) {
    return (
      <div className="vp-loading">
        <div style={{ textAlign: "center" }}>
          <div className="vp-spinner" />
          <Typography.Text type="secondary">Cargando solicitud...</Typography.Text>
        </div>
      </div>
    );
  }

  if (!fullRequestData) {
    return (
      <div className="vp-loading">
        <div style={{ textAlign: "center" }}>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            No se encontraron datos de la solicitud.
          </Typography.Text>
          <Button type="link" onClick={handleBack} style={{ color: "#ac4949" }}>Volver a solicitudes</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="vp-page">
      {/* ─── HEADER ─── */}
      <div className={tc("vp-header")}>
        <div className="vp-header-left">
          <Button size="small" icon={<ArrowLeftOutlined />} onClick={handleBack} />
          <Typography.Text strong style={{ fontSize: 13 }}>
            {fullRequestData.user?.username || fullRequestData.user?.accountName || "Usuario"}
          </Typography.Text>
          {fullRequestData.requestNumber && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              #{fullRequestData.requestNumber}
            </Typography.Text>
          )}
          <Tag
            className="vp-tag"
            color={
              fullRequestData.status === "PENDING" ? "blue"
              : fullRequestData.status === "APPROVED" ? "processing"
              : fullRequestData.status === "REJECTED" ? "error"
              : fullRequestData.status === "CANCELED" ? "default"
              : fullRequestData.status === "DEPOSIT_ERROR" ? "volcano"
              : fullRequestData.status === "COMPLETED" ? "success"
              : "default"
            }
          >
            {fullRequestData.status}
          </Tag>
          <Tag className="vp-tag" color="#722ed1">WITHDRAW</Tag>
        </div>

        <div className="vp-header-right">
          {!isReprocessing && (
            <Checkbox checked={showChangeAmount} onChange={(e) => setShowChangeAmount(e.target.checked)} disabled={isSubmitting}>
              <span style={{ fontSize: 12 }}>Modificar monto</span>
            </Checkbox>
          )}
          <Checkbox checked={notifyUser} onChange={(e) => setNotifyUser(e.target.checked)} disabled={isSubmitting}>
            <span style={{ fontSize: 12 }}>Notificar</span>
          </Checkbox>
          <Popover
            content={cancelFormContent}
            title="Cancelar Retiro"
            trigger="click"
            open={showCancelForm}
            onOpenChange={(v) => { setShowCancelForm(v); if (!v) { setShowCustomReason(false); cancelForm.resetFields(); } }}
            placement="bottomRight"
          >
            <Button
              danger
              size="small"
              icon={<CloseCircleOutlined />}
              disabled={isSubmitting || isCancelling}
            >
              Cancelar
            </Button>
          </Popover>
        </div>
      </div>

      {/* ─── MAIN: 3 columns ─── */}
      <div className="vp-main">
        {/* ─── LEFT: Bank Info (30%) ─── */}
        <div className={tc("vp-col")} style={{ width: "30%", minWidth: 260 }}>
          <div className="vp-scroll" style={{ padding: 16 }}>
            <div className={tc("vp-card")} style={{ borderLeft: "4px solid #1890ff" }}>
              <div className="vp-section-title" style={{ marginBottom: 16, opacity: 1 }}>
                <BankOutlined style={{ fontSize: 16, color: "#1890ff" }} />
                <span style={{ fontSize: 14 }}>Datos para Transferencia</span>
              </div>

              {/* Username */}
              <div className={tc("vp-row")}>
                <span className="vp-row-label">Usuario</span>
                <span className="vp-row-value">{fullRequestData.user?.username}</span>
              </div>

              {/* CBU/CVU */}
              <div className={tc("vp-row")}>
                <span className="vp-row-label">CBU/CVU</span>
                <span className="vp-row-value vp-row-value--mono">
                  <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {bankInfo.cbu}
                  </span>
                  <Tooltip title={copySuccess["cbu"] ? "Copiado!" : "Copiar"}>
                    <Button type="text" size="small" icon={<CopyOutlined />} className="vp-copy" onClick={() => copyToClipboard(bankInfo.cbu, "cbu")} />
                  </Tooltip>
                </span>
              </div>

              {/* Alias */}
              <div className={tc("vp-row")}>
                <span className="vp-row-label">Alias</span>
                <span className="vp-row-value vp-row-value--mono">
                  <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {bankInfo.alias}
                  </span>
                  <Tooltip title={copySuccess["alias"] ? "Copiado!" : "Copiar"}>
                    <Button type="text" size="small" icon={<CopyOutlined />} className="vp-copy" onClick={() => copyToClipboard(bankInfo.alias, "alias")} />
                  </Tooltip>
                </span>
              </div>

              {/* Titular */}
              <div className={tc("vp-row")}>
                <span className="vp-row-label"><UserOutlined style={{ marginRight: 4 }} />Titular</span>
                <span className="vp-row-value">
                  <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>
                    {bankInfo.holder}
                  </span>
                  <Tooltip title={copySuccess["holder"] ? "Copiado!" : "Copiar"}>
                    <Button type="text" size="small" icon={<CopyOutlined />} className="vp-copy" onClick={() => copyToClipboard(bankInfo.holder, "holder")} />
                  </Tooltip>
                </span>
              </div>

              {/* Divider */}
              <div className={tc("vp-divider").replace("vp-divider ", "")} />

              {/* Monto solicitado */}
              <div className={tc("vp-row")}>
                <span className="vp-row-value" style={{ fontWeight: 600 }}>Monto solicitado</span>
                <span className="vp-amount" style={{ color: "#1890ff" }}>
                  ${bankInfo.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ─── CENTER: History Data (35%) ─── */}
        <div className={tc("vp-col")} style={{ width: "35%", minWidth: 280 }}>
          <div className={tc("vp-section-head")}>
            <div className="vp-section-title" style={{ opacity: 1 }}>
              <HistoryOutlined style={{ color: "#ac4949" }} />
              <span>Historial de Operaciones</span>
            </div>
            <Button
              size="small"
              icon={<ReloadOutlined spin={isLoadingHistory} />}
              onClick={() => fetchHistory()}
              disabled={isLoadingHistory}
            >
              Actualizar
            </Button>
          </div>

          <div className="vp-scroll" style={{ padding: "8px 12px" }}>
            {isLoadingHistory ? (
              <div className="vp-empty">
                <Spin size="default" />
                <Typography.Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                  Cargando historial...
                </Typography.Text>
              </div>
            ) : historyData && historyData.length > 0 ? (
              <table className={tc("vp-table")}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Fecha</th>
                    <th style={{ textAlign: "left" }}>Op</th>
                    <th style={{ textAlign: "right" }}>Monto</th>
                    <th style={{ textAlign: "right" }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {historyData
                    .filter((item: any) => Array.isArray(item) && item.length >= 7)
                    .slice(0, 50)
                    .map((item: any, idx: number) => {
                      const date = new Date(item[0]);
                      const dateStr = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")} ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
                      return (
                        <tr key={idx}>
                          <td>{dateStr}</td>
                          <td>{item[3]}</td>
                          <td style={{ textAlign: "right" }}>{item[4]}</td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>{item[6]}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            ) : (
              <div className="vp-empty">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {historyData ? "Sin historial disponible" : "Haz click en Actualizar para cargar el historial"}
                </Typography.Text>
              </div>
            )}
          </div>
        </div>

        {/* ─── RIGHT: Upload + Submit ─── */}
        <div className="vp-col" style={{ flex: 1, minWidth: 0 }}>
          <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            <div className="vp-scroll" style={{ padding: "12px 16px" }}>
              {/* Reprocessing banner */}
              {isReprocessing && (
                <Alert
                  message="Reprocesando retiro rechazado"
                  description={<span><strong>Motivo:</strong> {rejectionReason}</span>}
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ marginBottom: 12, borderLeft: "4px solid #ff4d4f" }}
                />
              )}

              {/* Upload area with placeholder */}
              <Upload
                beforeUpload={() => false}
                fileList={fileList}
                onChange={handleFileChange}
                maxCount={1}
                disabled={isSubmitting}
                showUploadList={false}
              >
                {previewUrl ? (
                  <div className={tc("vp-upload-preview")}>
                    <img
                      src={previewUrl}
                      style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 4 }}
                      alt="Vista previa del comprobante"
                    />
                  </div>
                ) : (
                  <div className={tc("vp-upload-zone")}>
                    <UploadOutlined className={tc("vp-upload-icon")} />
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      Subir comprobante
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 2 }}>
                      Click para seleccionar archivo
                    </Typography.Text>
                  </div>
                )}
              </Upload>

              {/* Amount fields (when modifying) */}
              {(isReprocessing || showChangeAmount) && (
                <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                  <Form.Item
                    label={<span style={{ fontSize: 12 }}><DollarOutlined /> {isReprocessing ? "Monto" : "Nuevo Monto"}</span>}
                    name="amount"
                    rules={[{ required: true, message: "Monto requerido" }]}
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input type="number" step="0.01" placeholder="0.00" prefix="$" disabled={isSubmitting} />
                  </Form.Item>
                </div>
              )}
            </div>

            {/* Action bar - pinned to bottom */}
            <div className={tc("vp-action-bar")} style={{ justifyContent: "flex-end" }}>
              <Button
                type="primary"
                danger={isReprocessing}
                htmlType="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                style={{ height: 32 }}
              >
                {isReprocessing ? "Reprocesar Retiro" : "Completar Retiro"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {/* Panel Selector */}
      <PanelSelector
        isOpen={panelSelectorOpen}
        onClose={() => {
          setPanelSelectorOpen(false);
          setPendingPanelOperation({ type: null });
          setIsLoadingHistory(false);
        }}
        onPanelSelect={handlePanelSelect}
        panels={availablePanels}
        loading={isLoadingPanels}
        actionTitle="Seleccionar Panel"
        actionDescription="Selecciona un panel para continuar."
      />
    </div>
  );
};

export default WithdrawCompletionPage;
